import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Icons from "../components/icons";

import {
  Check,
  ChevronLeft,
  ChevronRight,
  Info,
  MoreVertical,
  X,
} from "lucide-react";
import { authFetch } from "../lib/api";
const noteCategories = [
  {
    key: "meeting",
    label: "Meetings",
    dot: "bg-[#FFAE00]",
    text: "text-[#D18B00]",
    bg: "bg-[#FFF8E1]",
    keywords: [
      "meeting",
      "call",
      "discussion",
      "discuss",
      "stakeholder",
      "client",
      "sync",
      "demo",
      "presentation",
      "review meeting",
      "standup",
      "interview",
      "appointment",
      "agenda",
    ],
  },
  {
    key: "work",
    label: "Work",
    dot: "bg-[#4379FE]",
    text: "text-[#4379FE]",
    bg: "bg-[#EEF2FF]",
    keywords: [
      "project",
      "task",
      "design",
      "sprint",
      "development",
      "bug",
      "dashboard",
      "kanban",
      "report",
      "timesheet",
      "deployment",
      "testing",
      "api",
      "review",
      "feature",
      "ticket",
      "client requirement",
    ],
  },
  {
    key: "personal",
    label: "Personal",
    dot: "bg-[#22C55E]",
    text: "text-[#22C55E]",
    bg: "bg-[#EAF7EE]",
    keywords: [
      "buy",
      "doctor",
      "birthday",
      "family",
      "vacation",
      "travel",
      "shopping",
      "bank",
      "renew",
      "pay bill",
      "friend",
      "exercise",
      "home",
      "grocery",
    ],
  },
];

const generalCategory = {
  key: "general",
  label: "General",
  dot: "bg-gray-400",
  text: "text-gray-600",
  bg: "bg-gray-50",
};

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// This router should be mounted in the backend as: app.use("/api/my-work", router)
const MY_WORK_TASKS_ENDPOINT = "/api/tasks/calendar";
const formatDateKey = (date) => {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDisplayDate = (dateKey) => {
  const [yyyy, mm, dd] = dateKey.split("-").map(Number);
  return `${String(dd).padStart(2, "0")} ${months[mm - 1]} ${yyyy}`;
};

const formatTime = (isoDate) =>
  new Date(isoDate).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const getCategoryForText = (text) => {
  const normalized = text.toLowerCase();

  return (
    noteCategories.find((category) =>
      category.keywords.some((keyword) => {
        const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return new RegExp(`\\b${escaped}\\b`, "i").test(normalized);
      }),
    ) || generalCategory
  );
};

const MyWork = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [tasksError, setTasksError] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [notesError, setNotesError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [activeNoteMenu, setActiveNoteMenu] = useState(null);
  const [showLegend, setShowLegend] = useState(false);
  // Tracks which day cell's "+N more tasks" popover is currently open
  const [activeTaskPopover, setActiveTaskPopover] = useState(null);
  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    const loadNotes = async () => {
      try {
        setNotesLoading(true);
        const res = await authFetch("/api/notes");
        const data = await res.json();
        if (data.success) {
          setNotes(data.data);
        } else {
          setNotesError(data.message || "Failed to load notes.");
        }
      } catch (err) {
        setNotesError("Failed to load notes.");
      } finally {
        setNotesLoading(false);
      }
    };
    loadNotes();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const monthParam = `${year}-${String(month + 1).padStart(2, "0")}`;

    const loadTasks = async () => {
      try {
        setTasksError(null);
        const response = await authFetch(
          `${MY_WORK_TASKS_ENDPOINT}?month=${monthParam}`,
          { signal: controller.signal },
        );
        const contentType = response.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          throw new Error(
            "The calendar API returned HTML instead of JSON. Check the backend URL and route.",
          );
        }
        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Failed to load calendar tasks.");
        }

        // Backend data is grouped as { "YYYY-MM-DD": [task, ...] }.
        // Keep the date key supplied by the server so no timezone conversion
        // can move a task to a neighbouring calendar day.
        const allTasks = Object.entries(result.data || {}).flatMap(
          ([date, dayTasks]) =>
            (Array.isArray(dayTasks) ? dayTasks : []).map((task) => ({
              ...task,
              date,
              title: `${task.project || "—"} | ${task.name || "Untitled task"}`,
            })),
        );

        setTasks(allTasks);
      } catch (error) {
        if (error.name !== "AbortError") {
          setTasks([]);
          setTasksError(error.message || "Failed to load calendar tasks.");
        }
      }
    };

    loadTasks();
    return () => controller.abort();
  }, [year, month]);

  // Close the task popover on month change
  useEffect(() => {
    setActiveTaskPopover(null);
  }, [year, month]);

  // Close the task popover on any click outside of it
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest("[data-task-popover]")) {
        setActiveTaskPopover(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // The popover is pinned to the viewport, so it would drift away from its
  // "+N" badge if the page scrolled or resized — close it instead. Scrolling
  // inside the popover's own list is ignored.
  useEffect(() => {
    if (!activeTaskPopover) return undefined;
    const close = (e) => {
      if (e?.target?.closest?.("[data-task-popover]")) return;
      setActiveTaskPopover(null);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [activeTaskPopover]);

  // Places the popover next to its badge but always fully on screen: it opens
  // downward when there is room and upward otherwise, and right-aligns to the
  // badge when it would run past the right edge (the Sunday column).
  const placeTaskPopover = (anchorRect) => {
    const margin = 8;
    const width = 260;
    const preferredHeight = 260;
    const spaceBelow = window.innerHeight - anchorRect.bottom - margin;
    const spaceAbove = anchorRect.top - margin;
    const openUp = spaceBelow < Math.min(preferredHeight, 160) && spaceAbove > spaceBelow;

    let left = anchorRect.left;
    if (left + width > window.innerWidth - margin) left = anchorRect.right - width;
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));

    return {
      left,
      width,
      maxHeight: Math.max(120, Math.min(preferredHeight, (openUp ? spaceAbove : spaceBelow) - 4)),
      ...(openUp
        ? { bottom: window.innerHeight - anchorRect.top + 4 }
        : { top: anchorRect.bottom + 4 }),
    };
  };
  const [taskPopoverStyle, setTaskPopoverStyle] = useState(null);

  const days = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const start = firstDay === 0 ? 6 : firstDay - 1;
    const calendarDays = [];

    for (let i = 0; i < start; i++) {
      calendarDays.push({ day: null, current: false, dateKey: `blank-${i}` });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      calendarDays.push({ day, current: true, dateKey: formatDateKey(date) });
    }

    while (calendarDays.length % 7 !== 0) {
      calendarDays.push({
        day: null,
        current: false,
        dateKey: `blank-end-${calendarDays.length}`,
      });
    }

    return calendarDays;
  }, [month, year]);

  const notesByDate = useMemo(
    () =>
      notes.reduce((acc, note) => {
        acc[note.date] = acc[note.date] || [];
        acc[note.date].push(note);
        return acc;
      }, {}),
    [notes],
  );

  const selectedNotes = selectedDate ? notesByDate[selectedDate] || [] : [];

  const getTasks = (dateKey) => tasks.filter((task) => task.date === dateKey);

  const getNoteCategoriesForDate = (dateKey) => {
    const categories = (notesByDate[dateKey] || []).map((note) =>
      getCategoryForText(note.text),
    );
    const uniqueKeys = new Set();

    return categories.filter((category) => {
      if (uniqueKeys.has(category.key)) return false;
      uniqueKeys.add(category.key);
      return true;
    });
  };

  const openNotes = (dateKey) => {
    setSelectedDate(dateKey);
    setNoteText("");
    setEditingNoteId(null);
    setEditingText("");
    setActiveNoteMenu(null);
    setShowLegend(false);
    setActiveTaskPopover(null);
  };

  const closeNotes = () => {
    setSelectedDate(null);
    setNoteText("");
    setEditingNoteId(null);
    setEditingText("");
    setActiveNoteMenu(null);
    setShowLegend(false);
  };

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text || !selectedDate) return;

    try {
      const res = await authFetch("/api/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selectedDate, text }),
      });
      const data = await res.json();
      if (data.success) {
        setNotes((prev) => [data.data, ...prev]);
        setNoteText("");
      }
    } catch (err) {
      // optionally show a toast/error message here
    }
  };

  const handleStartEdit = (note) => {
    setEditingNoteId(note._id);
    setEditingText(note.text);
    setActiveNoteMenu(null);
  };

  const handleSaveEdit = async (noteId) => {
    const text = editingText.trim();
    if (!text) return;

    try {
      const res = await authFetch(`/api/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const data = await res.json();
      if (data.success) {
        setNotes((prev) =>
          prev.map((note) => (note._id === noteId ? data.data : note)),
        );
        setEditingNoteId(null);
        setEditingText("");
      }
    } catch (err) {
      // optionally show a toast/error message here
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const res = await authFetch(`/api/notes/${noteId}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (data.success) {
        setNotes((prev) => prev.filter((note) => note._id !== noteId));
        setActiveNoteMenu(null);
        if (editingNoteId === noteId) {
          setEditingNoteId(null);
          setEditingText("");
        }
      }
    } catch (err) {
      // optionally show a toast/error message here
    }
  };

  return (
    <div className="p-6 bg-transparent min-h-screen font-sans">
      <div className="bg-white rounded-xl px-6 py-5 shadow-sm">
        <h1 className="text-[22px] font-semibold text-gray-800">My Work</h1>
        <p className="text-sm text-gray-700 mt-3 w-fit pb-1.5 border-b-2 border-blue-500">
          Calendar
        </p>
      </div>

      <div className="mt-4 bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex justify-end items-center px-6 py-3">
          <div className="flex items-center gap-4 text-gray-700">
            <button
              type="button"
              aria-label="Previous month"
              className="p-1 hover:text-blue-600"
              onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="w-[120px] text-center text-sm font-medium">
              {months[month]} {year}
            </span>
            <button
              type="button"
              aria-label="Next month"
              className="p-1 hover:text-blue-600"
              onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-y border-gray-100 text-xs text-gray-500 bg-white">
          {weekdays.map((day) => (
            <div
              key={day}
              className="border-r border-gray-100 py-2 text-center last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 border-b border-gray-200">
          {" "}
          {days.map((dayInfo) => {
            if (!dayInfo.day) {
              return (
                <div
                  key={dayInfo.dateKey}
                  className="h-[115px] border-r border-gray-100 bg-white last:border-r-0"
                />
              );
            }

            const isToday =
              dayInfo.dateKey === formatDateKey(today) && dayInfo.current;
            const dayTasks = getTasks(dayInfo.dateKey);
            const extraTasks = dayTasks.slice(1);
            const dateNoteCategories = getNoteCategoriesForDate(
              dayInfo.dateKey,
            );

            return (
              // Note: this is a div (not a button) because it now contains a
              // real <button> for the "+N" badge — buttons can't be nested.
              <div
                key={dayInfo.dateKey}
                role="button"
                tabIndex={0}
                onClick={() => openNotes(dayInfo.dateKey)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openNotes(dayInfo.dateKey);
                  }
                }}
                className={`h-[115px] border-r border-gray-100 px-2 py-1 relative text-left cursor-pointer hover:bg-blue-50/40 transition last:border-r-0
                ${!dayInfo.current ? "bg-gray-50 text-gray-400" : "bg-white"}`}
              >
                <span
                  className={`absolute top-2 right-2 text-xs px-1.5 py-[1px] rounded font-medium
                  ${isToday ? "bg-blue-500 text-white" : "text-gray-700"}`}
                >
                  {dayInfo.day.toString().padStart(2, "0")}
                </span>

                <div className="mt-7 space-y-1.5 overflow-visible">
                  {dateNoteCategories.length > 0 && (
                    <div className="flex items-center gap-1 justify-end">
                      {dateNoteCategories.slice(0, 4).map((category) => (
                        <span
                          key={category.key}
                          className={`h-2.5 w-2.5 rounded-full ${category.dot}`}
                          title={category.label}
                        />
                      ))}
                    </div>
                  )}

                  {dayTasks.length > 0 && (
                    <div className="flex items-center gap-1">
                      <div className="h-[23px] flex items-center bg-orange-400 text-white text-[10px] px-2 rounded-lg truncate min-w-0 flex-1">
                        {dayTasks[0].title}
                      </div>

                      {extraTasks.length > 0 && (
                        <div
                          className="relative shrink-0"
                          data-task-popover
                        >
                          <button
                            type="button"
                            aria-label={`Show ${extraTasks.length} more tasks`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTaskPopoverStyle(
                                placeTaskPopover(
                                  e.currentTarget.getBoundingClientRect(),
                                ),
                              );
                              setActiveTaskPopover((prev) =>
                                prev === dayInfo.dateKey
                                  ? null
                                  : dayInfo.dateKey,
                              );
                            }}
                            className="h-[23px] flex items-center justify-center bg-orange-400 text-white text-[10px] px-2 rounded-lg font-medium hover:bg-orange-500"
                          >
                            +{extraTasks.length}
                          </button>

                          {/* Rendered into <body> so the calendar card's
                              overflow-hidden cannot clip it. */}
                          {activeTaskPopover === dayInfo.dateKey &&
                            taskPopoverStyle &&
                            createPortal(
                              <div
                                data-task-popover
                                role="dialog"
                                aria-label={`${extraTasks.length} more tasks`}
                                className="fixed z-[60] overflow-y-auto rounded-lg border border-gray-100 bg-white px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.14)] font-sans"
                                style={taskPopoverStyle}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {extraTasks.map((task, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-start gap-1.5 py-1"
                                  >
                                    <span className="mt-[5px] h-1 w-1 shrink-0 rounded-full bg-orange-400" />
                                    <span className="text-[11px] leading-[15px] capitalize text-black break-words min-w-0">
                                      {task.title}
                                    </span>
                                  </div>
                                ))}
                              </div>,
                              document.body,
                            )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="h-4 bg-white" />
        <div className="border-b border-gray-200" />
      </div>

      {tasksError && (
        <p className="mt-2 text-sm text-red-500">{tasksError}</p>
      )}

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={() => navigate("/dashboard/kanban")}
          className="bg-[#4253C7] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#3645A8] transition"
        >
          Open Kanban Board
        </button>
      </div>

      {selectedDate && (
        <div
          className="fixed inset-0 bg-black/20 z-50 flex justify-end items-start"
          onClick={closeNotes}
        >
          <div
            className="relative h-[1024px] max-h-screen w-[484px] max-w-full bg-white overflow-y-auto shadow-2xl font-[Poppins]"
            style={{ fontFamily: "Poppins, sans-serif" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close notes"
              className="absolute right-8 top-8 h-[33px] w-[33px] flex items-center justify-center text-black/50 hover:text-black"
              onClick={closeNotes}
            >
              <X size={33} strokeWidth={2.4} />
            </button>

            <div className="mt-[93px] ml-8 flex h-[59px] w-[420px] items-end justify-between">
              <div>
                <p
                  className="h-[21px] text-[14px] font-normal leading-[21px] text-black"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "21px",
                  }}
                >
                  Notes for
                </p>
                <h2
                  className="mt-2 h-[30px] text-[20px] font-medium leading-[30px] text-black"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "20px",
                    fontWeight: 500,
                    lineHeight: "30px",
                  }}
                >
                  {formatDisplayDate(selectedDate)}
                </h2>
              </div>

             
            </div>

            <div className="relative ml-8 mt-10 h-[165px] w-[420px] rounded-lg bg-[#F1F5F9]">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Write a note....."
                className="absolute left-6 top-6 h-[94px] w-[348px] resize-none bg-transparent text-[14px] font-normal leading-[21px] text-gray-800 outline-none placeholder:text-[#7F7F7F]"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "21px",
                }}
              />
              <button
                type="button"
                aria-label="Save note"
                onClick={handleAddNote}
                disabled={!noteText.trim()}
                className="absolute bottom-3 right-3 h-6 w-6 text-black disabled:text-gray-300"
              >
                <Icons.Tick />
              </button>
            </div>

            <div className="ml-8 mt-16 w-[420px]">
         <div className="flex items-start justify-between relative">
  <h3
    className="relative h-[23px] w-[60px] text-[14px] font-normal leading-[21px] text-black after:absolute after:left-px after:top-[21px] after:h-0.5 after:w-[25px] after:rounded-xl after:bg-[#4379FE]"
    style={{
      fontFamily: "Poppins, sans-serif",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "21px",
    }}
  >
    All Notes
  </h3>

  <div className="relative">
    <button
      type="button"
      aria-label="Show note categories"
      onClick={() => setShowLegend((prev) => !prev)}
      className="flex h-6 w-6 items-center justify-center text-[#666666] hover:text-gray-800"
    >
      <Info size={20} strokeWidth={1.5} />
    </button>

    {showLegend && (
      <>
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowLegend(false)}
        />
        <div className="absolute right-0 top-8 z-20 w-[137px] rounded-lg border border-black/[0.22] bg-white p-2 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
          {[
            { label: "Work", dot: "bg-[#4379FE]" },
            { label: "Meetings", dot: "bg-[#FFAE00]" },
            { label: "Personal", dot: "bg-[#22C55E]" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2 py-1 px-1">
              <span className={`h-2 w-2 shrink-0 rounded-full ${item.dot}`} />
              <span
                className="text-[12px] font-normal capitalize text-black"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
</div>

              {notesLoading && (
                <p className="mt-4 text-sm text-gray-400">Loading notes...</p>
              )}
              {notesError && (
                <p className="mt-4 text-sm text-red-500">{notesError}</p>
              )}

              <div className="mt-5 space-y-5 pb-8">
                {!notesLoading && selectedNotes.length === 0 && (
                  <div
                    className="w-[420px] rounded-lg border border-dashed border-gray-200 p-8 text-center text-[14px] text-gray-500"
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "21px",
                    }}
                  >
                    No notes yet.
                  </div>
                )}

                {selectedNotes.map((note) => {
                  const category = getCategoryForText(note.text);
                  const isEditing = editingNoteId === note._id;

                  return (
                    <div
                      key={note._id}
                      className={`relative flex h-auto min-h-[96px] w-[420px] flex-col rounded-lg ${category.bg} px-6 pb-5 pt-6`}
                    >
                      <div className="absolute right-5 top-5">
                        <button
                          type="button"
                          aria-label="Note actions"
                          className="flex h-3 w-3 items-center justify-center rounded text-black"
                          onClick={() =>
                            setActiveNoteMenu(
                              activeNoteMenu === note._id ? null : note._id,
                            )
                          }
                        >
                          <MoreVertical size={12} strokeWidth={3} />
                        </button>

                        {activeNoteMenu === note._id && (
                          <>
                            <div
                              className="fixed inset-0 z-[9]"
                              onClick={() => setActiveNoteMenu(null)}
                            />
                            <div className="absolute right-1 top-3 z-10 h-10 w-[52px] overflow-hidden rounded bg-white text-[8px] leading-3 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
                              <button
                                type="button"
                                className="flex h-5 w-full items-center px-1 text-left text-black hover:bg-gray-50"
                                style={{
                                  fontFamily: "Poppins, sans-serif",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                  lineHeight: "12px",
                                }}
                                onClick={() => handleStartEdit(note)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="flex h-5 w-full items-center border-t border-black/[0.03] px-1 text-left text-red-500 hover:bg-red-50"
                                style={{
                                  fontFamily: "Poppins, sans-serif",
                                  fontSize: "12px",
                                  fontWeight: 400,
                                  lineHeight: "12px",
                                }}
                                onClick={() => handleDeleteNote(note._id)}
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>

                      {isEditing ? (
                        <div className="pr-12">
                          <textarea
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            className="w-full min-h-[90px] resize-y rounded-md bg-white/70 p-3 text-[14px] leading-[21px] text-gray-800 outline-none"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              lineHeight: "21px",
                            }}
                          />
                          <div className="mt-3 flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleSaveEdit(note._id)}
                              disabled={!editingText.trim()}
                              className="inline-flex items-center gap-2 rounded-md bg-blue-500 px-3 py-2 text-sm text-white disabled:bg-gray-300"
                            >
                              <Check size={15} />
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingNoteId(null);
                                setEditingText("");
                              }}
                              className="rounded-md bg-white px-3 py-2 text-sm text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex gap-4 pr-[65px]">
                            <span
                              className={`mt-2 h-2 w-2 shrink-0 rounded-full ${category.dot}`}
                            />
                            <p
                              className={`max-w-[307px] whitespace-pre-wrap break-words text-[14px] font-normal leading-[21px] ${category.text}`}
                              style={{
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: "21px",
                              }}
                            >
                              {note.text}
                            </p>
                          </div>
                          <p
                            className="mt-4 pl-8 text-[10px] font-normal leading-[15px] text-[#7F7F7F]"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "10px",
                              fontWeight: 400,
                              lineHeight: "15px",
                            }}
                          >
                            {formatTime(note.createdAt)}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyWork;
